import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import campaignService from './campaignService';
import notificationService from './notificationService';
import userService from './userService';
import trackingLinkService from './trackingLinkService';

/**
 * Campaign Participant Service - Handles all operations related to campaign participants (influencers)
 */
const campaignParticipantService = {
  /**
   * Apply to a campaign
   * @param {string} campaignId - Campaign ID
   * @param {string} influencerId - Influencer user ID
   * @param {object} applicationData - Application data (note, etc.)
   * @returns {Promise<object>} - Created participant object with ID
   */
  applyToCampaign: async (campaignId, influencerId, applicationData = {}) => {
    try {
      // Check if user has already applied to this campaign
      const existingApplication = await campaignParticipantService.getParticipantByCampaignAndInfluencer(
        campaignId, 
        influencerId
      );
      
      if (existingApplication) {
        throw new Error('You have already applied to this campaign');
      }
      
      // Check if campaign exists and is active
      const campaign = await campaignService.getCampaignById(campaignId);
      if (!campaign) {
        throw new Error('Campaign not found');
      }
      
      if (campaign.status !== 'active') {
        throw new Error('Campaign is not accepting applications');
      }
      
      if (campaign.filledSpots >= campaign.totalSpots) {
        throw new Error('Campaign is already full');
      }
      
      const participantsRef = collection(db, 'campaignParticipants');
      
      // Set default fields
      const defaultData = {
        campaignId,
        influencerId,
        status: 'pending',
        appliedAt: serverTimestamp(),
        statusUpdatedAt: serverTimestamp(),
        submissionStatus: 'not_submitted',
        paymentStatus: 'not_paid',
        analytics: {
          reach: 0,
          engagement: 0,
          clicks: 0,
          conversions: 0
        }
      };
      
      // Merge with provided data
      const dataToSave = { ...defaultData, ...applicationData };
      
      const docRef = await addDoc(participantsRef, dataToSave);
      
      // Update campaign analytics
      await campaignService.updateCampaignAnalytics(campaignId, {
        applications: (campaign.analytics?.applications || 0) + 1
      });
      
      // Return the created participant with its ID
      return { id: docRef.id, ...dataToSave };
    } catch (error) {
      console.error('Error applying to campaign:', error);
      throw error;
    }
  },

  /**
   * Get participant by ID
   * @param {string} participantId - Participant ID
   * @returns {Promise<object|null>} - Participant data or null if not found
   */
  getParticipantById: async (participantId) => {
    try {
      const participantRef = doc(db, 'campaignParticipants', participantId);
      const participantDoc = await getDoc(participantRef);
      
      if (participantDoc.exists()) {
        return { id: participantDoc.id, ...participantDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting participant:', error);
      throw error;
    }
  },

  /**
   * Get participant by campaign and influencer IDs
   * @param {string} campaignId - Campaign ID
   * @param {string} influencerId - Influencer user ID
   * @returns {Promise<object|null>} - Participant data or null if not found
   */
  getParticipantByCampaignAndInfluencer: async (campaignId, influencerId) => {
    try {
      const participantsRef = collection(db, 'campaignParticipants');
      const q = query(
        participantsRef, 
        where('campaignId', '==', campaignId),
        where('influencerId', '==', influencerId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting participant by campaign and influencer:', error);
      throw error;
    }
  },

  /**
   * Update participant status
   * @param {string} participantId - Participant ID
   * @param {string} status - New status ('approved', 'rejected', etc.)
   * @param {string} feedback - Optional feedback from brand
   * @returns {Promise<object>} - Updated participant object
   */
  updateParticipantStatus: async (participantId, status, feedback = '') => {
    try {
      const participantRef = doc(db, 'campaignParticipants', participantId);
      const participantDoc = await getDoc(participantRef);
      
      if (!participantDoc.exists()) {
        throw new Error(`Participant with ID ${participantId} not found`);
      }
      
      const participant = participantDoc.data();
      const campaignId = participant.campaignId;
      const influencerId = participant.influencerId;
      
      // Update participant status
      const updateData = {
        status,
        statusUpdatedAt: serverTimestamp(),
        brandFeedback: feedback
      };
      
      await updateDoc(participantRef, updateData);
      
      // Update campaign analytics if status is approved
      if (status === 'approved') {
        const campaign = await campaignService.getCampaignById(campaignId);
        
        await campaignService.updateCampaignAnalytics(campaignId, {
          approvedApplications: (campaign.analytics?.approvedApplications || 0) + 1
        });
        
        // Update filled spots
        await campaignService.updateCampaign(campaignId, {
          filledSpots: (campaign.filledSpots || 0) + 1
        });

        // Generate unique tracking link for approved influencer
        try {
          const trackingLink = await trackingLinkService.generateTrackingLink(
            campaignId,
            influencerId,
            participantId
          );
          
          // Update participant with tracking link information
          await updateDoc(participantRef, {
            trackingLinkId: trackingLink.id,
            trackingCode: trackingLink.trackingCode,
            trackingUrl: trackingLink.trackingUrl
          });
          

        } catch (trackingError) {
          console.error('Error generating tracking link:', trackingError);
          // Don't throw the error to avoid disrupting the approval process
        }
      }
      
      // Send notification to influencer about application status
      try {
        // Get campaign details for notification
        const campaign = await campaignService.getCampaignById(campaignId);
        if (campaign) {
          await notificationService.createApplicationStatusNotification(
            influencerId,
            campaignId,
            campaign.title || 'Campaign',
            status
          );

        }
      } catch (notificationError) {
        console.error('Error sending application status notification:', notificationError);
        // Don't throw the error to avoid disrupting the main flow
      }
      
      // Return the updated participant
      return { 
        id: participantId, 
        ...participant, 
        ...updateData 
      };
    } catch (error) {
      console.error('Error updating participant status:', error);
      throw error;
    }
  },

  /**
   * Update participant submission status
   * @param {string} participantId - Participant ID
   * @param {string} submissionStatus - New submission status
   * @param {string} submissionId - Content submission ID
   * @returns {Promise<object>} - Updated participant object
   */
  updateSubmissionStatus: async (participantId, submissionStatus, submissionId = null, feedback = '') => {
    try {
      const participantRef = doc(db, 'campaignParticipants', participantId);
      const participantDoc = await getDoc(participantRef);
      
      if (!participantDoc.exists()) {
        throw new Error(`Participant with ID ${participantId} not found`);
      }
      
      const participant = participantDoc.data();
      const campaignId = participant.campaignId;
      const influencerId = participant.influencerId;
      
      const updateData = {
        submissionStatus,
        statusUpdatedAt: serverTimestamp()
      };
      
      if (submissionId) {
        updateData.submissionId = submissionId;
      }
      
      if (feedback) {
        updateData.submissionFeedback = feedback;
      }
      
      await updateDoc(participantRef, updateData);
      
      // Send notification to influencer about content approval status
      try {
        // Get campaign details for notification
        const campaign = await campaignService.getCampaignById(campaignId);
        if (campaign && ['approved', 'rejected', 'revision_requested'].includes(submissionStatus)) {
          await notificationService.createContentApprovalNotification(
            influencerId,
            campaignId,
            campaign.title || 'Campaign',
            submissionId,
            submissionStatus
          );

        }
      } catch (notificationError) {
        console.error('Error sending content approval notification:', notificationError);
        // Don't throw the error to avoid disrupting the main flow
      }
      
      // Return the updated participant
      return { 
        id: participantId, 
        ...participant, 
        ...updateData 
      };
    } catch (error) {
      console.error('Error updating submission status:', error);
      throw error;
    }
  },

  /**
   * Update participant payment status
   * @param {string} participantId - Participant ID
   * @param {string} paymentStatus - New payment status
   * @param {string} paymentId - Payment ID
   * @returns {Promise<object>} - Updated participant object
   */
  updatePaymentStatus: async (participantId, paymentStatus, paymentId = null, amount = 0) => {
    try {
      const participantRef = doc(db, 'campaignParticipants', participantId);
      const participantDoc = await getDoc(participantRef);
      
      if (!participantDoc.exists()) {
        throw new Error(`Participant with ID ${participantId} not found`);
      }
      
      const participant = participantDoc.data();
      const campaignId = participant.campaignId;
      const influencerId = participant.influencerId;
      
      const updateData = {
        paymentStatus,
        statusUpdatedAt: serverTimestamp()
      };
      
      if (paymentId) {
        updateData.paymentId = paymentId;
      }
      
      await updateDoc(participantRef, updateData);
      
      // Send notification to influencer about payment status
      try {
        if (paymentStatus === 'paid' && paymentId) {
          // Get campaign details for notification
          const campaign = await campaignService.getCampaignById(campaignId);
          if (campaign) {
            await notificationService.createPaymentNotification(
              influencerId,
              campaignId,
              campaign.title || 'Campaign',
              paymentId,
              amount || campaign.compensation || 0
            );

          }
        }
      } catch (notificationError) {
        console.error('Error sending payment notification:', notificationError);
        // Don't throw the error to avoid disrupting the main flow
      }
      
      // Return the updated participant
      return { 
        id: participantId, 
        ...participant, 
        ...updateData 
      };
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  },

  /**
   * Get participants by campaign ID
   * @param {string} campaignId - Campaign ID
   * @param {object} options - Query options (status, orderBy, etc.)
   * @returns {Promise<Array>} - Array of participant objects
   */
  getParticipantsByCampaignId: async (campaignId, options = {}) => {
    try {
      const participantsRef = collection(db, 'campaignParticipants');
      
      // Build query
      let q = query(participantsRef, where('campaignId', '==', campaignId));
      
      // Add status filter if provided
      if (options.status) {
        q = query(q, where('status', '==', options.status));
      }
      
      // Add submission status filter if provided
      if (options.submissionStatus) {
        q = query(q, where('submissionStatus', '==', options.submissionStatus));
      }
      
      // Add ordering
      const orderByField = options.orderBy || 'appliedAt';
      const orderDirection = options.orderDirection || 'desc';
      q = query(q, orderBy(orderByField, orderDirection));
      
      const querySnapshot = await getDocs(q);
      
      const participants = [];
      querySnapshot.forEach((doc) => {
        participants.push({ id: doc.id, ...doc.data() });
      });
      
      return participants;
    } catch (error) {
      console.error('Error getting participants by campaign ID:', error);
      throw error;
    }
  },

  /**
   * Get campaigns by influencer ID
   * @param {string} influencerId - Influencer user ID
   * @param {object} options - Query options (status, orderBy, etc.)
   * @returns {Promise<Array>} - Array of participant objects with campaign data
   */
  getCampaignsByInfluencerId: async (influencerId, options = {}) => {
    try {
      const participantsRef = collection(db, 'campaignParticipants');
      
      // Build query
      let q = query(participantsRef, where('influencerId', '==', influencerId));
      
      // Add status filter if provided
      if (options.status) {
        q = query(q, where('status', '==', options.status));
      }
      
      // Add submission status filter if provided
      if (options.submissionStatus) {
        q = query(q, where('submissionStatus', '==', options.submissionStatus));
      }
      
      // Add ordering
      const orderByField = options.orderBy || 'appliedAt';
      const orderDirection = options.orderDirection || 'desc';
      q = query(q, orderBy(orderByField, orderDirection));
      
      const querySnapshot = await getDocs(q);
      
      const participantsWithCampaigns = [];
      
      // Get all campaign data for the participants
      const campaignPromises = querySnapshot.docs.map(async (doc) => {
        const participant = { id: doc.id, ...doc.data() };
        const campaign = await campaignService.getCampaignById(participant.campaignId);
        
        participantsWithCampaigns.push({
          ...participant,
          campaign
        });
      });
      
      await Promise.all(campaignPromises);
      
      // Sort by the original order
      participantsWithCampaigns.sort((a, b) => {
        if (orderDirection === 'desc') {
          return b[orderByField] - a[orderByField];
        } else {
          return a[orderByField] - b[orderByField];
        }
      });
      
      return participantsWithCampaigns;
    } catch (error) {
      console.error('Error getting campaigns by influencer ID:', error);
      throw error;
    }
  },

  /**
   * Update participant analytics
   * @param {string} participantId - Participant ID
   * @param {object} analyticsData - Analytics data to update
   * @returns {Promise<object>} - Updated analytics object
   */
  updateParticipantAnalytics: async (participantId, analyticsData) => {
    try {
      const participantRef = doc(db, 'campaignParticipants', participantId);
      const participantDoc = await getDoc(participantRef);
      
      if (!participantDoc.exists()) {
        throw new Error(`Participant with ID ${participantId} not found`);
      }
      
      const currentAnalytics = participantDoc.data().analytics || {
        reach: 0,
        engagement: 0,
        clicks: 0,
        conversions: 0
      };
      
      // Merge with new analytics data
      const updatedAnalytics = { ...currentAnalytics, ...analyticsData };
      
      await updateDoc(participantRef, {
        analytics: updatedAnalytics,
        statusUpdatedAt: serverTimestamp()
      });
      
      // Update campaign analytics as well
      const campaignId = participantDoc.data().campaignId;
      const campaign = await campaignService.getCampaignById(campaignId);
      
      // Calculate total reach and engagement across all participants
      const participants = await campaignParticipantService.getParticipantsByCampaignId(
        campaignId, 
        { status: 'approved' }
      );
      
      const totalReach = participants.reduce((sum, p) => sum + (p.analytics?.reach || 0), 0);
      const totalEngagement = participants.reduce((sum, p) => sum + (p.analytics?.engagement || 0), 0);
      
      await campaignService.updateCampaignAnalytics(campaignId, {
        totalReach,
        totalEngagement
      });
      
      return updatedAnalytics;
    } catch (error) {
      console.error('Error updating participant analytics:', error);
      throw error;
    }
  }
};

export default campaignParticipantService;
